import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { getFormInfo } from '@/features/fhirResources/services/FhirR4Client'
import { OrderFormInfoData, Sex } from '@/types/RequisitionFormTypes'
import { raceEthnicityFromFhirPatient } from '@/utils/fhirPatientRaceEthnicity'
import { toProperCaseName } from '@/utils/freeTextValidation'
import type { Practitioner } from 'fhir/r4'

/**
 * FHIR does not order practitioner.telecom; using telecom[0] for "phone" duplicates email when Epic lists email first.
 * Prefer system=phone, then work / mobile use (aligned with server-side practitioner_parsing).
 */
function getPractitionerPhoneFromTelecom(practitioner: Pick<Practitioner, 'telecom'>): string {
  const telecoms = practitioner.telecom
  if (!telecoms?.length) {
    return ''
  }

  const phones = telecoms.filter((t) => t.system === 'phone')
  if (phones.length) {
    const work = phones.find((t) => t.use === 'work')
    if (work?.value) {
      return work.value
    }
    const mobile = phones.find((t) => t.use === 'mobile')
    if (mobile?.value) {
      return mobile.value
    }
    return phones[0]?.value ?? ''
  }

  // Legacy payloads with a single contact missing system: use value only if not explicitly email
  const first = telecoms[0]
  if (first?.system === 'email') {
    return ''
  }
  return first?.value ?? ''
}

export async function extractFormOrderData(state: string): Promise<OrderFormInfoData> {
  const epicAuthClient = EpicAuthClient.getInstance()
  const result = await getFormInfo(state, epicAuthClient)
  const context = epicAuthClient.getContext(state)
  const mrnIdentifierSystem = context.mrnIdentifierSystem
  const parsed = raceEthnicityFromFhirPatient(result.patient as unknown as Record<string, unknown>)
  const race = result.patientRace ?? parsed.race
  const ethnicity = result.patientEthnicity ?? parsed.ethnicity
  const sex =
    result.patient.gender?.toLowerCase() == 'unknown' ? Sex.Unknown : (result.patient.gender as Sex) || Sex.Unknown

  // Transform FormInfoResponse to OrderFormInfoData
  const transformedData: OrderFormInfoData = {
    patient: {
      id: (result.patient.id as string) || '',
      name: {
        firstName: toProperCaseName(result.patient.name?.at(0)?.given?.at(0) || ''),
        middleName: toProperCaseName(result.patient.name?.at(0)?.given?.at(1) || ''),
        lastName: toProperCaseName(result.patient.name?.at(0)?.family || ''),
      },
      mrn: result.patient.identifier?.find((identifier) => identifier.system === mrnIdentifierSystem)?.value || '',
      sex: sex,
      birthDate: result.patient.birthDate ? new Date(result.patient.birthDate as string) : null,
      ...(race ? { race } : {}),
      ...(ethnicity ? { ethnicity } : {}),
    },
    medicalProfessionalInfo: {
      npi: result.practitioner_npi || '',
      name: toProperCaseName(result.practitioner.name?.at(0)?.text || ''),
      email: result.practitioner_email || '',
    },
    institution: {
      id: result.organization?.id || context.orgId || '',
      name: result.organization?.name || context.clientName || '',
      address: {
        street: result.organization?.address?.at(0)?.line?.at(0) || '',
        city: result.organization?.address?.at(0)?.city || '',
        state: result.organization?.address?.at(0)?.state || '',
        zip: result.organization?.address?.at(0)?.postalCode || '',
        country: result.organization?.address?.at(0)?.country || '',
      },
    },
    orderingProvider: {
      name: {
        firstName: toProperCaseName(result.practitioner.name?.at(0)?.given?.at(0) || ''),
        lastName: toProperCaseName(result.practitioner.name?.at(0)?.family || ''),
      },
      roleTitle: result.practitioner.qualification?.at(0)?.code?.coding?.at(0)?.display || '',
      phone: getPractitionerPhoneFromTelecom(result.practitioner),
      email: result.practitioner_email || '',
      id: result.practitioner.id || '',
      npi: result.practitioner_npi || '',
    },
    providerType: context.providerType || '',
    organizationId: context.orgId || result.organization?.id || '',
    organizationName: context.clientName || result.organization?.name || '',
  }
  return transformedData
}
